import { Icon } from '@efira/ui';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@efira/ui/components/ui/breadcrumb';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@efira/ui/components/ui/sidebar';
import { Link, useParams, useSearchParams } from 'react-router';

import { FeedbackModal } from '../feedback/FeedbackModal';

import { Placeholder } from '../Placeholder';
import { Analytics, ANALYTICS_TABS } from './Analytics';
import { ConsoleModal } from './ConsoleModal';
import { History } from './History';
import { Affiliate } from './Affiliate';
import { BillingCancelled, BillingDone } from './BillingReturn';
import { PLAN_CONTEXTS, Plan, planContext } from './Plan';
import { Switcher } from './Switcher';
import { Billing } from '../settings/Billing';
import { GiftFx } from '../settings/GiftFx';
import { Profile } from '../settings/Profile';
import { TikTokAccounts } from '../settings/TikTokAccounts';
import { Watermark } from '../settings/Watermark';
import { SoundLibrary } from '../settings/SoundLibrary';
import { StreamSection } from '../settings/StreamSection';

/**
 * Окна модального слоя - каркас этапа 1.
 *
 * Здесь заложены адреса и поведение слоя; содержимое приходит на этапе 2 из
 * реестра. Разделы и вкладки перечислены уже сейчас: их состав - часть
 * адреса, а значит часть каркаса, а не наполнения.
 */

/**
 * Семь разделов настроек эталона (карта экранов, 7.1).
 *
 * МЕНЮ ПОКАЗЫВАЕТ ТО, ЧТО ЕСТЬ. Пункт, ведущий в «скоро», - это обещание,
 * данное меню и не выполненное содержимым. С единицы 6 готовы все семь.
 *
 * ПОРЯДОК - ЭТАЛОННЫЙ: кто я, откуда события, что видно в кадре, что
 * звучит, эфир, эффекты, чем платим. Свой «осмысленный» порядок держался
 * ровно до первой сверки: эталон здесь и есть спецификация, а расхождение
 * в порядке пунктов человек, пришедший от конкурента, читает как пропажу
 * раздела. Подпись каждого пункта несёт значок: список из семи строк
 * одинаковым кеглем читается построчно, а со значками - взглядом.
 */
export const SETTINGS_SECTIONS = [
  { id: 'profile', title: 'Профиль', icon: 'person', ready: true },
  { id: 'tiktok', title: 'Аккаунты TikTok', icon: 'channel', ready: true },
  { id: 'watermark', title: 'Водяной знак', icon: 'shield', ready: true },
  { id: 'djpad', title: 'Звуки и пады', icon: 'pads', ready: true },
  { id: 'stream', title: 'Эфир', icon: 'live', ready: true },
  { id: 'giftfx', title: 'Эффекты подарков', icon: 'gift', ready: true },
  { id: 'billing', title: 'Оплата', icon: 'coins', ready: true },
] as const;

/* Вкладки аналитики объявлены рядом с самой аналитикой (`Analytics.tsx`):
   список вкладок - её свойство, а не свойство слоя окон. */
export { ANALYTICS_TABS };

export function SettingsModal() {
  const { section } = useParams();

  const current =
    SETTINGS_SECTIONS.find((item) => item.id === (section ?? 'profile')) ?? SETTINGS_SECTIONS[0];

  /*
   * НА ТЕЛЕФОНЕ ОКНО НАСТРОЕК - ОДНА КОЛОНКА.
   *
   * До единицы 17 обе колонки оставались и на 390 px: меню забирало 208 из
   * 358, содержимому доставалось около ста. Заголовок «Профиль» обрезался
   * до «Профил», кнопка «Сохранить» уходила за край, абзацы ломались по
   * одному слову (аудит единицы 16, находка 03).
   *
   * Разделяет ЭКРАНЫ адрес, а не состояние: `/app/settings` - список,
   * `/app/settings/<раздел>` - раздел с возвратом к списку. На широком
   * экране видны обе колонки, как и раньше.
   */
  const picked = section !== undefined;

  return (
    <ConsoleModal
      title="Настройки"
      /*
       * ОКНО НАСТРОЕК - ВТОРАЯ РАБОЧАЯ ПОВЕРХНОСТЬ, А НЕ ДИАЛОГ.
       *
       * Высота фиксирована: окно, растущее по содержимому, у «Профиля»
       * занимало половину экрана, у библиотеки звуков - всё, и меню
       * прыгало между разделами. Прокручивается только содержимое.
       */
      dialogClassName="max-w-5xl"
      /* Тон подписи не `/70`: в светлой теме 12 px этого тона дают контраст
         4,31 при требуемых 4,5 - axe ловит это на каждом разделе. */
      header={
        <span className="text-foreground/80 text-xs font-semibold tracking-[0.12em] uppercase">
          Настройки
        </span>
      }
    >
      {/*
        ОКНО НАСТРОЕК СОБРАНО ПО БЛОКУ `sidebar-13` (единица 19).

        ЧТО ВЗЯТО ИЗ БЛОКА: `SidebarProvider items-start` с колонкой
        разделов на `SidebarMenuButton`, хлебные крошки «Настройки › раздел»
        в шапке содержимого, прокрутка только у содержимого.

        ЧТО НАШЕ: семь разделов и их значки, само содержимое разделов,
        поведение на телефоне.

        ПОЧЕМУ НЕ ЦЕЛИКОМ ПО БЛОКУ НА ТЕЛЕФОНЕ. Блок ниже `md` просто
        ПРЯЧЕТ колонку разделов - и человек остаётся в разделе без способа
        сменить его. Это не мобильное поведение, а его отсутствие. Наш
        разбор по адресу (`/app/settings` - список, `/app/settings/<id>` -
        раздел) остаётся, но собран компонентами блока: возврат к списку
        даёт ссылка «Настройки» В ХЛЕБНЫХ КРОШКАХ, а не своя кнопка
        «‹ Разделы» из единицы 17 - у блока для «где я и как обратно» есть
        свой орган, и второй такой же рядом был бы тем самым «два входа в
        одно действие».

        ФОКУС. `SidebarProvider` - не фокус-ловушка и своего контекста не
        заводит: ловушка остаётся у окна, `Escape` закрывает окно, а
        горячая клавиша боковой колонки (Ctrl/Cmd+B) при `collapsible="none"`
        не делает ничего. Проверено сценарием `console-modals.spec.ts`.
      */}
      <SidebarProvider
        /* `bg-transparent` - НЕ КОСМЕТИКА. Обёртка провайдера красится
           `--sidebar`, и содержимое раздела оказалось на чужой
           поверхности: axe намерил недостаточный контраст у подписей
           12 px в «Эффектах подарков». Поверхность окна задаёт окно;
           колонка разделов остаётся на своей - ради шва между ними блок
           и взят. */
        className="min-h-[26rem] items-start bg-transparent"
      >
        <Sidebar
          collapsible="none"
          data-settings="nav"
          aria-label="Разделы настроек"
          className={`bg-transparent md:flex ${picked ? 'hidden w-full' : 'flex w-full'} md:w-52`}
        >
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {SETTINGS_SECTIONS.filter((item) => item.ready).map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={item.id === current.id}>
                        <Link
                          to={`/app/settings/${item.id}`}
                          aria-current={item.id === current.id ? 'page' : undefined}
                        >
                          <Icon name={item.icon} size="sm" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <main className={`flex min-h-0 min-w-0 flex-1 flex-col ${picked ? '' : 'hidden md:flex'}`}>
          <header className="flex shrink-0 items-center gap-2 px-4 pb-3">
            <Breadcrumb aria-label="Где вы в настройках">
              <BreadcrumbList>
                <BreadcrumbItem>
                  {/* На широком экране это просто «где я»; на телефоне -
                      ещё и возврат к списку разделов. */}
                  <BreadcrumbLink asChild>
                    <Link to="/app/settings" data-settings="back">
                      Настройки
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{current.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>

          {/*
            Содержимое прокручивается НЕЗАВИСИМО от колонки разделов:
            список звуков длиннее окна, и уезжающее вместе с ним меню -
            это потеря опоры.

            ЗАТУХАНИЯ У НИЖНЕЙ КРОМКИ ЗДЕСЬ НЕТ, хотя в окнах тарифа и
            партнёрки оно есть. Разделы настроек - это списки ОРГАНОВ
            УПРАВЛЕНИЯ, а не текст: кнопка, попавшая в зону затухания,
            рисуется полупрозрачной, и axe намерил у неё контраст 1,75
            при 4,5 - белая подпись на приглушённом зелёном. Приём,
            который помогает тексту, у кнопок отнимает читаемость.
          */}
          <div data-settings="content" className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-6">
            {current.id === 'profile' ? (
              <Profile />
            ) : current.id === 'tiktok' ? (
              <TikTokAccounts />
            ) : current.id === 'watermark' ? (
              <Watermark />
            ) : current.id === 'billing' ? (
              <Billing />
            ) : current.id === 'djpad' ? (
              <SoundLibrary />
            ) : current.id === 'stream' ? (
              <StreamSection />
            ) : current.id === 'giftfx' ? (
              <GiftFx />
            ) : (
              /* Ветка недостижима: с единицы 6 готовы все семь разделов.
                 Оставлена намеренно - следующий раздел, который заведут,
                 покажет честную табличку, а не пустоту. */
              <Placeholder
                title="Настройки"
                hint="Раздел в работе: он приедет своей единицей, а не заглушкой в меню."
                registry={['settings.nav']}
              />
            )}
          </div>
        </main>
      </SidebarProvider>
    </ConsoleModal>
  );
}

export function AnalyticsModal() {
  return (
    /* Высота окна не зависит от наличия данных: заглушка и числа живут в
       окне одного размера, иначе оно прыгает при каждом фильтре. */
    <ConsoleModal title="Аналитика" windowKey="analytics" dialogClassName="max-w-5xl">
      <Analytics />
    </ConsoleModal>
  );
}

export function HistoryModal() {
  return (
    <ConsoleModal title="История эфиров" windowKey="history" dialogClassName="max-w-3xl">
      <History />
    </ConsoleModal>
  );
}

export function AffiliateModal() {
  return (
    <ConsoleModal
      title="Партнёрская программа"
      windowKey="affiliate"
      dialogClassName="max-w-3xl"
      contentClassName="@container"
    >
      <Affiliate />
    </ConsoleModal>
  );
}

export function GamesModal() {
  return (
    /* Окно уже и фиксировано по высоте: прокручивается только сетка, шапка
       с поиском и категориями стоит на месте. Растущее по содержимому окно
       прыгало бы в высоту при каждом фильтре. */
    <ConsoleModal title="Смена игры" windowKey="switcher" dialogClassName="max-w-3xl">
      <Switcher />
    </ConsoleModal>
  );
}

/** Форма обращения: два контекста одного окна, различаются адресом. */
export function FeedbackRoute() {
  const { kind } = useParams();
  return <FeedbackModal kind={kind === 'issue' ? 'issue' : 'idea'} />;
}

/**
 * Окно тарифа. Заголовок ОДИН - и он зависит от того, откуда пришли.
 *
 * Находка владельца: три строки об одном подряд - «Тариф», «Что даёт Pro»,
 * «Возможности Pro и сколько он стоит». Рубрика окна и заголовок раздела
 * внутри него отвечали на один вопрос. Теперь заголовок окна и есть
 * заголовок контекста, а в теле остаётся одна строка пояснения.
 */
export function PlanModal() {
  const [params] = useSearchParams();
  const context = planContext(params.get('from'));

  return (
    <ConsoleModal
      title="Тариф"
      heading={PLAN_CONTEXTS[context].title}
      windowKey="plan"
      dialogClassName="max-w-2xl"
      titleData={{ 'data-plan': 'title' }}
    >
      <Plan />
    </ConsoleModal>
  );
}

/**
 * Страницы возврата с оплаты - окнами консоли, а не отдельными экранами.
 *
 * Человек возвращается туда, откуда ушёл: консоль остаётся под окном, и
 * закрыть его - значит просто продолжить работу.
 */
export function BillingDoneModal() {
  return (
    <ConsoleModal
      title="Оплата"
      heading="Возврат с оплаты"
      windowKey="billing-done"
      dialogClassName="max-w-lg"
    >
      <BillingDone />
    </ConsoleModal>
  );
}

export function BillingCancelledModal() {
  return (
    <ConsoleModal
      title="Оплата"
      heading="Возврат с оплаты"
      windowKey="billing-cancelled"
      dialogClassName="max-w-lg"
    >
      <BillingCancelled />
    </ConsoleModal>
  );
}
